import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronLeft,
  LogOut,
  Menu as MenuIcon,
  Settings,
  X,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/features/auth/store';
import { useRestaurantStore } from '@/features/restaurants/store';
import { useRestaurantLanguage } from '@/shared/hooks/useRestaurantLanguage';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { PrinterStatusBadge } from '@/shared/components/PrinterStatusBadge';
import { NotificationBell } from '@/features/notifications';
import { DeauthorizedBanner } from '@/features/payments';
import { AppSidebar, SidebarToggle, type SidebarMode } from './AppSidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { useIsNative } from '@/shared/hooks/useIsNative';
import { OrderAlertDialog } from '@/features/orders/components/OrderAlertDialog';

interface AppShellProps {
  /** When true, render the shell as a layout route (uses Outlet). When children are
   *  provided, render them instead — useful for the few routes still wrapping
   *  manually. */
  children?: React.ReactNode;
}

/**
 * The shell every authenticated page sits inside: top bar + persistent sidebar +
 * main outlet. The sidebar adapts to whether a `:restaurantId` is in the URL —
 * restaurant mode in that context, account mode otherwise — so the rail is
 * always there as the user moves between billing, payouts, settings, etc.
 */
export function AppShell({ children }: AppShellProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId } = useParams();
  const isNative = useIsNative();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant);
  const sidebarCollapsed = useRestaurantStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useRestaurantStore((s) => s.toggleSidebar);
  const setSidebarCollapsed = useRestaurantStore((s) => s.setSidebarCollapsed);

  // Apply the active restaurant's language to the whole shell so that the
  // OrderAlertDialog, NotificationBell, and all toasts use the correct locale
  // even before RestaurantLayout has a chance to run its own effect.
  useRestaurantLanguage(currentRestaurant?.defaultLanguage);

  const [mobileOpen, setMobileOpen] = useState(false);

  // On native (iPad landscape) the screen can be wide enough for a persistent
  // sidebar even though isNative is true. Track viewport width so we show the
  // sidebar on iPad landscape (≥1024px) exactly as on desktop web.
  // IMPORTANT: must be declared before canGoBack to avoid the temporal dead zone.
  const [isWideViewport, setIsWideViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsWideViewport(e.matches);
    mq.addEventListener('change', handler);
    setIsWideViewport(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Show the persistent sidebar when: desktop web always, OR native on a wide
  // viewport (iPad landscape). iPhone native never gets the persistent sidebar.
  // On native iPhone, show a back chevron instead of the hamburger whenever
  // there are SPA history entries to return to.
  const canGoBack = isNative && !isWideViewport && (window.history.state?.idx ?? 0) > 0;

  const showPersistentSidebar = !isNative || isWideViewport;

  const mode: SidebarMode = restaurantId ? 'restaurant' : 'account';

  // Auto-collapse on /orders* so the Kanban board has the full width. The user
  // can override by clicking the toggle (handled below).
  const isOrdersRoute = /\/orders(\/|$)/.test(location.pathname);
  const [autoCollapsedByRoute, setAutoCollapsedByRoute] = useState(isOrdersRoute);

  useEffect(() => {
    setAutoCollapsedByRoute(isOrdersRoute);
  }, [isOrdersRoute]);

  const effectiveCollapsed = sidebarCollapsed || autoCollapsedByRoute;

  // Close the mobile drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSidebarToggle = () => {
    if (autoCollapsedByRoute && effectiveCollapsed) {
      // User wants to expand despite the route override — break the override.
      setAutoCollapsedByRoute(false);
      if (sidebarCollapsed) setSidebarCollapsed(false);
    } else {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    // On native: fixed viewport height so the layout never exceeds the screen —
    // the header and bottom nav are always visible while main scrolls internally.
    // On web: min-height so the window can grow and window-scroll works normally.
    <div
      className={cn(
        'bg-background flex flex-col',
        isNative ? 'h-screen h-[100dvh]' : 'min-h-screen min-h-[100dvh]',
      )}
    >
      {/* Top bar
          paddingTop: safe-area-inset-top lets the header background fill the iOS
          status bar / Android camera cutout. On web it resolves to 0 on desktop
          and to the notch height on mobile Safari — both correct.
          On native the header is not sticky (layout is fixed-height, it's always
          visible). On web it's sticky so it survives window-level scrolling. */}
      <header
        className={cn(
          'z-40 border-b border-border bg-card/80 backdrop-blur-sm shrink-0',
          !isNative && 'sticky top-0',
        )}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 lg:px-6 gap-2">
          {/* Left — mobile menu + logo */}
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            {!isWideViewport && (
              canGoBack ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-xl shrink-0"
                  onClick={() => navigate(-1)}
                  aria-label="Go back"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-xl shrink-0"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                >
                  <MenuIcon className="h-[18px] w-[18px]" />
                </Button>
              )
            )}
            <Link to="/app" className="flex items-center gap-2 min-w-0">
              <img src="/logo.svg" alt="Restroos" className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg shrink-0" />
              <span className="font-semibold hidden xs:block truncate tracking-tight">
                <span className="text-foreground">Res</span><span className="text-accent">troos</span>
              </span>
            </Link>
          </div>

          {/* Center — restaurant identity.
              Outer wrapper keys on restaurantId (URL param, synchronous) so the
              flex-row sibling count is stable for the entire lifetime of a
              restaurant page. currentRestaurant (Zustand, set async via
              RestaurantLayout useEffect) only controls what renders INSIDE the
              wrapper — prevents insertBefore on the right-side siblings when the
              Zustand useSyncExternalStore flush races a concurrent render. */}
          {restaurantId && (
            <div className="flex-1 flex justify-center min-w-0 px-1 sm:px-2">
              {currentRestaurant && (
                <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 h-9 sm:h-10 max-w-[200px] sm:max-w-[260px]">
                  {currentRestaurant.logoUrl ? (
                    <img
                      src={currentRestaurant.logoUrl}
                      alt=""
                      className="w-5 h-5 sm:w-6 sm:h-6 rounded-md object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md gradient-warm flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                      {currentRestaurant.name.charAt(0)}
                    </div>
                  )}
                  <span className="truncate text-xs sm:text-sm font-medium max-w-[100px] sm:max-w-[180px]">
                    {currentRestaurant.name}
                  </span>
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full shrink-0',
                      currentRestaurant.isStoreOpen ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                    )}
                    aria-hidden
                  />
                </div>
              )}
            </div>
          )}

          {/* Right — printer (electron only), notifications, language, user menu */}
          <div className="flex-shrink-0 flex items-center gap-1 sm:gap-1.5">
            <PrinterStatusBadge />
            {(restaurantId ?? currentRestaurant?.id) && (
              <NotificationBell restaurantId={(restaurantId ?? currentRestaurant?.id)!} />
            )}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1.5 h-9 sm:h-10 px-1.5 sm:px-2 rounded-xl hover:bg-muted/60">
                  <Avatar className="w-7 h-7 sm:w-8 sm:h-8">
                    <AvatarFallback className="bg-accent/15 text-accent font-semibold text-xs sm:text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-sm font-medium truncate max-w-[110px]">
                    {user?.name}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/app/settings')} className="gap-2">
                  <Settings className="w-4 h-4" />
                  {t('app.accountSettings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="sm:hidden" />
                <DropdownMenuItem
                  className="sm:hidden gap-2"
                  onClick={() => i18n.changeLanguage('de')}
                >
                  <span className="text-base">🇩🇪</span>
                  Deutsch
                  {i18n.language?.startsWith('de') && <span className="ml-auto text-emerald-600 text-sm">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="sm:hidden gap-2"
                  onClick={() => i18n.changeLanguage('en')}
                >
                  <span className="text-base">🇬🇧</span>
                  English
                  {i18n.language?.startsWith('en') && <span className="ml-auto text-emerald-600 text-sm">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4" />
                  {t('app.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <DeauthorizedBanner />
      <OrderAlertDialog />

      {/* Body — on native: overflow-hidden so main is the only scroller;
                  on web: normal flow so the window scrolls. */}
      <div className={cn('flex flex-1 min-h-0', isNative && 'overflow-hidden')}>
        {/* Mobile drawer — available on all platforms (native uses bottom nav for primary nav, but drawer provides full sidebar access) */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm lg:hidden"
                onClick={() => setMobileOpen(false)}
              />
              <motion.aside
                initial={{ x: -300 }}
                animate={{ x: 0 }}
                exit={{ x: -300 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed left-0 top-0 bottom-0 z-50 w-[280px] bg-card border-r border-border lg:hidden flex flex-col overflow-hidden"
                style={{
                  paddingTop: 'env(safe-area-inset-top)',
                  paddingBottom: 'env(safe-area-inset-bottom)',
                }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-3 right-3 h-8 w-8 z-10"
                  style={{ top: 'calc(0.75rem + env(safe-area-inset-top))' }}
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </Button>
                <AppSidebar
                  mode={mode}
                  collapsed={false}
                  onNavigate={() => setMobileOpen(false)}
                />
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Desktop sidebar — always on web; on native only when viewport is wide (iPad landscape) */}
        {showPersistentSidebar && (
          <div
            className={cn(
              'hidden lg:block relative shrink-0 sticky top-16 self-stretch',
              'h-[calc(100vh-4rem)] h-[calc(100dvh-4rem)]',
              'transition-[width] duration-200 ease-out',
              effectiveCollapsed ? 'w-16' : 'w-[260px]',
            )}
          >
            <aside className="h-full bg-card border-r border-border flex flex-col">
              <AppSidebar mode={mode} collapsed={effectiveCollapsed} />
            </aside>
            <SidebarToggle collapsed={effectiveCollapsed} onToggle={handleSidebarToggle} />
          </div>
        )}

        {/* Main content — on native it's the scroll container (overscroll-contain
            prevents the iOS rubber-band from propagating to the window). Sticky
            children inside (e.g. the orders filter bar) stick relative to this
            element, which is exactly what we want. */}
        <main
          className={cn(
            'flex-1 min-w-0 bg-background',
            isNative
              ? 'overflow-y-auto overscroll-y-contain'
              : 'min-h-0',
            // On mobile web and narrow native (iPhone), fixed bottom nav overlays content — compensate
            (!isNative || !isWideViewport) && 'pb-16 lg:pb-0',
          )}
        >
          {children ?? <Outlet />}
        </main>
      </div>

      {/* Bottom tab navigation — hidden on wide viewports (desktop sidebar takes over) */}
      {!isWideViewport && (
        <div
          className={cn(
            'lg:hidden shrink-0',
            !isNative && 'fixed bottom-0 inset-x-0 z-40',
          )}
        >
          <MobileBottomNav mode={mode} restaurantId={restaurantId} />
        </div>
      )}
    </div>
  );
}

export default AppShell;
